/**
 * Demo Data for Karebe React
 * Streamlined local catalog seeded from the current inventory list.
 */

import {
  generatedDemoCategories,
  generatedDemoProducts,
} from './local-inventory-catalog';

export interface DemoUser {
  id: string;
  email: string;
  password: string;
  role: 'super-admin' | 'admin' | 'rider' | 'customer';
  name: string;
  phone?: string;
  branchId?: string;
  avatar?: string;
}

export const demoUsers: DemoUser[] = [
  {
    id: '00000000-0000-0000-0000-000000000001',
    email: 'admin@karebe.local',
    password: 'adminlemon1234',
    role: 'super-admin',
    name: 'Admin User',
    phone: '+254712345678',
  },
  {
    id: 'user-admin-001',
    email: 'wangige@karebe.local',
    password: 'wangigelemon1234',
    role: 'admin',
    name: 'Wangige Admin',
    phone: '+254723456789',
    branchId: 'branch-wangige',
  },
  {
    id: 'user-rider-001',
    email: 'rider@karebe.local',
    password: 'riderlemon1234',
    role: 'rider',
    name: 'Demo Rider',
    phone: '+254725678901',
    branchId: 'branch-wangige',
  },
  {
    id: 'user-customer-001',
    email: 'customer@karebe.local',
    password: 'customerlemon1234',
    role: 'customer',
    name: 'Sarah Wanjiku',
    phone: '+254734567890',
  },
];

export interface DemoBranch {
  id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  isActive: boolean;
  coordinates?: { lat: number; lng: number };
}

export const demoBranches: DemoBranch[] = [
  {
    id: 'branch-wangige',
    name: 'Wangige Main',
    address: 'Wangige Town Centre, Off Waiyaki Way, Kiambu County',
    phone: '+254720123456',
    email: 'wangige@karebe.com',
    isActive: true,
    coordinates: { lat: -1.2195, lng: 36.7088 },
  },
  {
    id: 'branch-karura',
    name: 'Karura Branch',
    address: 'Karura Shopping Centre, Kiambu Road',
    phone: '+254720123457',
    email: 'karura@karebe.com',
    isActive: true,
    coordinates: { lat: -1.2305, lng: 36.8169 },
  },
  {
    id: 'branch-city',
    name: 'City Center',
    address: 'Moi Avenue, Nairobi CBD',
    phone: '+254720123458',
    email: 'city@karebe.com',
    isActive: true,
    coordinates: { lat: -1.2863, lng: 36.8172 },
  },
];

export interface DemoCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  image?: string;
  sortOrder: number;
}

export const demoCategories: DemoCategory[] = generatedDemoCategories;

export interface DemoProductVariant {
  id: string;
  size: string;
  volumeMl: number;
  price: number;
  comparePrice?: number;
  stock: number;
  sku: string;
  barcode?: string;
}

export interface DemoProduct {
  id: string;
  name: string;
  slug: string;
  description: string;
  categoryId: string;
  brand: string;
  country: string;
  alcoholContent: number;
  images: string[];
  variants: DemoProductVariant[];
  tags: string[];
  isActive: boolean;
  isFeatured: boolean;
  rating: number;
  reviewCount: number;
  createdAt: string;
}

export const demoProducts: DemoProduct[] = generatedDemoProducts;

export type OrderStatus =
  | 'pending'
  | 'confirmed'
  | 'processing'
  | 'ready'
  | 'out_for_delivery'
  | 'delivered'
  | 'cancelled';

export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded';
export type PaymentMethod = 'mpesa' | 'card' | 'cash';

export interface DemoOrderItem {
  productId: string;
  productName: string;
  variantId: string;
  size: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

export interface DemoOrder {
  id: string;
  orderNumber: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  deliveryAddress: string;
  deliveryNotes?: string;
  branchId: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod;
  items: DemoOrderItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  mpesaCode?: string;
  riderId?: string;
  riderName?: string;
  createdAt: string;
  updatedAt: string;
  deliveredAt?: string;
}

function requireProduct(name: string): DemoProduct {
  const product = demoProducts.find((item) => item.name === name);
  if (!product) {
    throw new Error(`Missing demo product: ${name}`);
  }
  return product;
}

function requireVariant(productName: string, size: string) {
  const product = requireProduct(productName);
  const variant = product.variants.find((item) => item.size === size);

  if (!variant) {
    throw new Error(`Missing demo variant: ${productName} ${size}`);
  }

  return { product, variant };
}

function createOrderItem(productName: string, size: string, quantity: number): DemoOrderItem {
  const { product, variant } = requireVariant(productName, size);

  return {
    productId: product.id,
    productName: product.name,
    variantId: variant.id,
    size: variant.size,
    quantity,
    unitPrice: variant.price,
    subtotal: variant.price * quantity,
  };
}

export const demoOrders: DemoOrder[] = [
  {
    id: 'order-001',
    orderNumber: 'KRB-260406-001',
    customerId: 'user-customer-001',
    customerName: 'Sarah Wanjiku',
    customerPhone: '+254734567890',
    customerEmail: 'customer@karebe.local',
    deliveryAddress: 'Muthiga Estate, House 45, Waiyaki Way',
    deliveryNotes: 'Call when you reach the gate',
    branchId: 'branch-wangige',
    status: 'delivered',
    paymentStatus: 'paid',
    paymentMethod: 'mpesa',
    items: [
      createOrderItem('SMIRNOFF', '750ML', 1),
      createOrderItem('TUSKER', 'CAN', 6),
    ],
    subtotal:
      createOrderItem('SMIRNOFF', '750ML', 1).subtotal +
      createOrderItem('TUSKER', 'CAN', 6).subtotal,
    deliveryFee: 200,
    total:
      createOrderItem('SMIRNOFF', '750ML', 1).subtotal +
      createOrderItem('TUSKER', 'CAN', 6).subtotal +
      200,
    mpesaCode: 'MPESA123456',
    riderId: 'user-rider-001',
    riderName: 'Demo Rider',
    createdAt: '2026-04-05T10:30:00Z',
    updatedAt: '2026-04-05T11:05:00Z',
    deliveredAt: '2026-04-05T11:00:00Z',
  },
  {
    id: 'order-002',
    orderNumber: 'KRB-260406-002',
    customerId: 'user-customer-001',
    customerName: 'Sarah Wanjiku',
    customerPhone: '+254734567890',
    customerEmail: 'customer@karebe.local',
    deliveryAddress: 'Karura, Off Kiambu Road, Plot 12',
    branchId: 'branch-karura',
    status: 'out_for_delivery',
    paymentStatus: 'paid',
    paymentMethod: 'mpesa',
    items: [
      createOrderItem('BLACK LABEL', '750ML', 1),
      createOrderItem('BAILEYS', '750ML', 1),
    ],
    subtotal:
      createOrderItem('BLACK LABEL', '750ML', 1).subtotal +
      createOrderItem('BAILEYS', '750ML', 1).subtotal,
    deliveryFee: 250,
    total:
      createOrderItem('BLACK LABEL', '750ML', 1).subtotal +
      createOrderItem('BAILEYS', '750ML', 1).subtotal +
      250,
    mpesaCode: 'MPESA789012',
    riderId: 'user-rider-001',
    riderName: 'Demo Rider',
    createdAt: '2026-04-06T08:15:00Z',
    updatedAt: '2026-04-06T08:30:00Z',
  },
  {
    id: 'order-003',
    orderNumber: 'KRB-260406-003',
    customerId: 'user-customer-001',
    customerName: 'Sarah Wanjiku',
    customerPhone: '+254734567890',
    customerEmail: 'customer@karebe.local',
    deliveryAddress: 'Westlands, ABC Place, Office 302',
    branchId: 'branch-city',
    status: 'pending',
    paymentStatus: 'pending',
    paymentMethod: 'cash',
    items: [
      createOrderItem('4TH STREET', '5 LITER', 1),
      createOrderItem('MONSTER ENERGY', 'REGULAR', 2),
    ],
    subtotal:
      createOrderItem('4TH STREET', '5 LITER', 1).subtotal +
      createOrderItem('MONSTER ENERGY', 'REGULAR', 2).subtotal,
    deliveryFee: 300,
    total:
      createOrderItem('4TH STREET', '5 LITER', 1).subtotal +
      createOrderItem('MONSTER ENERGY', 'REGULAR', 2).subtotal +
      300,
    createdAt: '2026-04-06T09:45:00Z',
    updatedAt: '2026-04-06T09:45:00Z',
  },
];

export interface DemoDeliveryAssignment {
  id: string;
  orderId: string;
  riderId: string;
  riderName: string;
  status: 'assigned' | 'picked_up' | 'in_transit' | 'delivered' | 'failed';
  pickupBranchId: string;
  pickupBranchName: string;
  deliveryAddress: string;
  customerName: string;
  customerPhone: string;
  assignedAt: string;
  pickedUpAt?: string;
  deliveredAt?: string;
  notes?: string;
}

export const demoDeliveries: DemoDeliveryAssignment[] = [
  {
    id: 'delivery-001',
    orderId: 'order-001',
    riderId: 'user-rider-001',
    riderName: 'Demo Rider',
    status: 'delivered',
    pickupBranchId: 'branch-wangige',
    pickupBranchName: 'Wangige Main',
    deliveryAddress: 'Muthiga Estate, House 45, Waiyaki Way',
    customerName: 'Sarah Wanjiku',
    customerPhone: '+254734567890',
    assignedAt: '2026-04-05T10:35:00Z',
    pickedUpAt: '2026-04-05T10:45:00Z',
    deliveredAt: '2026-04-05T11:00:00Z',
  },
  {
    id: 'delivery-002',
    orderId: 'order-002',
    riderId: 'user-rider-001',
    riderName: 'Demo Rider',
    status: 'in_transit',
    pickupBranchId: 'branch-karura',
    pickupBranchName: 'Karura Branch',
    deliveryAddress: 'Karura, Off Kiambu Road, Plot 12',
    customerName: 'Sarah Wanjiku',
    customerPhone: '+254734567890',
    assignedAt: '2026-04-06T08:30:00Z',
    pickedUpAt: '2026-04-06T08:45:00Z',
    notes: 'Customer asked for a call before drop-off.',
  },
];

export interface DemoCartItem {
  productId: string;
  variantId: string;
  quantity: number;
  addedAt: string;
}

export const demoCart: DemoCartItem[] = [
  {
    productId: requireProduct('SMIRNOFF').id,
    variantId: requireVariant('SMIRNOFF', '1/2').variant.id,
    quantity: 1,
    addedAt: '2026-04-06T09:00:00Z',
  },
  {
    productId: requireProduct('TUSKER').id,
    variantId: requireVariant('TUSKER', 'CAN').variant.id,
    quantity: 3,
    addedAt: '2026-04-06T09:05:00Z',
  },
];

export const DEMO_STORAGE_KEYS = {
  USERS: 'karebe_demo_users',
  BRANCHES: 'karebe_demo_branches',
  CATEGORIES: 'karebe_demo_categories',
  PRODUCTS: 'karebe_demo_products',
  ORDERS: 'karebe_demo_orders',
  DELIVERIES: 'karebe_demo_deliveries',
  CART: 'karebe_demo_cart',
  SEEDED: 'karebe_demo_seeded',
  SEED_DATE: 'karebe_demo_seed_date',
} as const;

export const demoSettings = {
  appName: 'Karebe Wine & Spirits',
  currency: 'KES',
  currencySymbol: 'KSh',
  deliveryFeeBase: 200,
  deliveryFeePerKm: 50,
  minOrderAmount: 500,
  maxDeliveryDistanceKm: 20,
  mpesaPaybill: '123456',
  supportPhone: '+254720123456',
  supportEmail: 'support@karebe.com',
} as const;

export function formatPrice(price: number): string {
  return `${demoSettings.currencySymbol} ${price.toLocaleString('en-KE')}`;
}

export function getTotalProductsCount(): number {
  return demoProducts.length;
}

export function getTotalVariantsCount(): number {
  return demoProducts.reduce((total, product) => total + product.variants.length, 0);
}

export function getTotalStockValue(): number {
  return demoProducts.reduce(
    (total, product) =>
      total +
      product.variants.reduce(
        (variantTotal, variant) => variantTotal + variant.price * variant.stock,
        0
      ),
    0
  );
}

export function getOrdersByStatus(status: OrderStatus): DemoOrder[] {
  return demoOrders.filter((order) => order.status === status);
}

export function getPendingDeliveries(): DemoDeliveryAssignment[] {
  return demoDeliveries.filter((delivery) => delivery.status !== 'delivered');
}

export function getLowStockProducts(): DemoProduct[] {
  return demoProducts.filter((product) =>
    product.variants.some((variant) => variant.stock < 20)
  );
}
